import { useCallback, useEffect, useRef, useState } from 'react';
import { isElectron } from 'utils/environment';
import { getSessionId } from 'utils/authStorage';
import { type ManualNotes } from 'hooks/useManualNotes';
import type { DiagnosedIcd } from 'types/result';
import { normalizeDiagnosedIcdEntry } from 'widgets/results/diagnosis.utils';

// Below this, we just flag processing as "slower than usual" but keep waiting for icd_codes.
const ICD_SLOW_HINT_MS = 30_000;
// Last-resort disconnect if icd_codes never arrives. Comfortably above the backend's own
// CORTI_TIMEOUT_PREDICT_CODES_SECONDS (60s) so we don't cut off codes that would still arrive.
const ICD_GIVE_UP_MS = 90_000;
const PAUSE_ACK_TIMEOUT_MS = 3_000;

export type IcdStatus = 'idle' | 'pending' | 'ready';

const readResumedState = (message: Record<string, unknown>): ResumedState | null => {
  if (typeof message.elapsedSeconds !== 'number' || typeof message.notes !== 'object') return null;
  return {
    elapsedSeconds: message.elapsedSeconds,
    notes: message.notes as ManualNotes,
    languageCode: typeof message.languageCode === 'string' ? message.languageCode : null,
  };
};

export interface WebSocketErrorState {
  code: string;
  correlationId?: string;
}

export interface ReconnectEpisode {
  startedAt: number;
  reasonCode: string;
}

export interface ResumedState {
  elapsedSeconds: number;
  notes: ManualNotes;
  languageCode: string | null;
}

export interface StartResult {
  interactionId: string;
  reattached: boolean;
  resumed: ResumedState | null;
}

export interface RecordingMetadata {
  interactionId?: string;
  language?: string;
  anamnesisId?: string;
  visitReasonId?: string;
  medatixxId?: string | null;
  fullname?: string | null;
  birthdate?: string | null;
  notes?: ManualNotes;
  withoutRecording?: boolean;
  sameRecorder?: boolean;
}

interface UseRecordingWebSocketReturn {
  connect: () => Promise<void>;
  start: (metadata: RecordingMetadata) => Promise<StartResult>;
  pause: () => Promise<void>;
  sendChunk: (chunk: Blob) => Promise<void>;
  sendNotes: (notes: ManualNotes) => void;
  requestTranscription: () => Promise<void>;
  end: () => Promise<unknown>;
  disconnect: () => void;
  consumeServerError: () => WebSocketErrorState | null;
  isConnected: boolean;
  isProcessing: boolean;
  upstreamReconnect: ReconnectEpisode | null;
  icdCodes: DiagnosedIcd[] | null;
  icdStatus: IcdStatus;
  icdProcessingSlow: boolean;
  icdCodesReceivedAt: number | null;
  icdResultId: string | null;
}

const apiBase = import.meta.env.VITE_API_BASE_URL as string;
const url = new URL(apiBase);
const WEBSOCKET_URL = `${url.protocol === 'https:' ? 'wss:' : 'ws:'}//${url.host}/docs/conversations/recording/`;

export function useRecordingWebSocket(): UseRecordingWebSocketReturn {
  const webSocketRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [upstreamReconnect, setUpstreamReconnect] = useState<ReconnectEpisode | null>(null);
  const [icdCodes, setIcdCodes] = useState<DiagnosedIcd[] | null>(null);
  const [icdStatus, setIcdStatus] = useState<IcdStatus>('idle');
  const [icdProcessingSlow, setIcdProcessingSlow] = useState(false);
  const [icdCodesReceivedAt, setIcdCodesReceivedAt] = useState<number | null>(null);
  const [icdResultId, setIcdResultId] = useState<string | null>(null);
  const icdSlowHintTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const icdGiveUpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingStartResolveRef = useRef<((result: StartResult) => void) | null>(null);
  const pendingStartRejectRef = useRef<((reason: unknown) => void) | null>(null);
  const pendingTranscriptionResolveRef = useRef<(() => void) | null>(null);
  const pendingTranscriptionRejectRef = useRef<((reason: unknown) => void) | null>(null);
  const pendingResolveRef = useRef<((value: unknown) => void) | null>(null);
  const pendingRejectRef = useRef<((reason: unknown) => void) | null>(null);
  const serverErrorRef = useRef<WebSocketErrorState | null>(null);
  // The backend acks every pause, in order, so the oldest waiting pause owns the next ack.
  const pendingPauseAcksRef = useRef<(() => void)[]>([]);

  const consumeServerError = useCallback((): WebSocketErrorState | null => {
    const error = serverErrorRef.current;
    serverErrorRef.current = null;
    return error;
  }, []);

  const disconnect = useCallback(() => {
    serverErrorRef.current = null;
    setUpstreamReconnect(null);
    if (icdSlowHintTimeoutRef.current) {
      clearTimeout(icdSlowHintTimeoutRef.current);
      icdSlowHintTimeoutRef.current = null;
    }
    if (icdGiveUpTimeoutRef.current) {
      clearTimeout(icdGiveUpTimeoutRef.current);
      icdGiveUpTimeoutRef.current = null;
    }
    setIcdCodes((current) => current ?? []);
    setIcdStatus((current) => (current === 'pending' ? 'ready' : current));
    const ws = webSocketRef.current;
    if (ws) {
      ws.onopen = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.close();
      webSocketRef.current = null;
    }
    setIsConnected(false);
    setIsProcessing(false);
    [...pendingPauseAcksRef.current].forEach((settle) => settle());
    if (pendingStartRejectRef.current) {
      const reject = pendingStartRejectRef.current;
      pendingStartResolveRef.current = null;
      pendingStartRejectRef.current = null;
      reject({ code: 'connection_closed' });
    }
    if (pendingTranscriptionRejectRef.current) {
      const reject = pendingTranscriptionRejectRef.current;
      pendingTranscriptionResolveRef.current = null;
      pendingTranscriptionRejectRef.current = null;
      reject({ code: 'connection_closed' });
    }
    if (pendingRejectRef.current) {
      const reject = pendingRejectRef.current;
      pendingRejectRef.current = null;
      pendingResolveRef.current = null;
      reject({ code: 'connection_closed' });
    }
  }, []);

  const connect = useCallback(async () => {
    if (webSocketRef.current) disconnect();

    const url = WEBSOCKET_URL;
    serverErrorRef.current = null;

    // Electron cannot set custom HTTP headers on WebSocket upgrades, so we pass
    // the session key as a subprotocol instead.
    let webSocket: WebSocket;
    if (isElectron) {
      const sessionId = await getSessionId();
      if (!sessionId) throw new Error('No session ID available');
      webSocket = new WebSocket(url, ['session', sessionId]);
    } else {
      webSocket = new WebSocket(url);
    }
    webSocketRef.current = webSocket;
    setIcdCodes(null);
    setIcdStatus('pending');
    setIcdProcessingSlow(false);
    setIcdCodesReceivedAt(null);
    setIcdResultId(null);

    return new Promise<void>((resolve, reject) => {
      let opened = false;

      webSocket.onopen = () => {
        opened = true;
        setIsConnected(true);
        resolve();
      };

      webSocket.onerror = () => {
        setIsConnected(false);
        reject(new Error('WebSocket connection failed'));
      };

      webSocket.onclose = () => {
        setIsConnected(false);
        setIsProcessing(false);
        setUpstreamReconnect(null);
        webSocketRef.current = null;
        [...pendingPauseAcksRef.current].forEach((settle) => settle());
        if (!opened) {
          reject(new Error('WebSocket connection failed'));
          return;
        }
        if (pendingStartRejectRef.current) {
          pendingStartRejectRef.current({ code: 'connection_closed' });
          pendingStartResolveRef.current = null;
          pendingStartRejectRef.current = null;
        }
        if (pendingTranscriptionRejectRef.current) {
          pendingTranscriptionRejectRef.current({ code: 'connection_closed' });
          pendingTranscriptionResolveRef.current = null;
          pendingTranscriptionRejectRef.current = null;
        }
        if (pendingRejectRef.current) {
          pendingRejectRef.current({ code: 'connection_closed' });
          pendingRejectRef.current = null;
          pendingResolveRef.current = null;
        }
      };

      webSocket.onmessage = (event: MessageEvent) => {
        let message: Record<string, unknown>;
        try {
          message = JSON.parse(event.data as string) as Record<string, unknown>;
        } catch {
          return;
        }
        if (message.type === 'started') {
          const id = typeof message.interactionId === 'string' ? message.interactionId : '';
          pendingStartResolveRef.current?.({
            interactionId: id,
            reattached: message.reattached === true,
            resumed: readResumedState(message),
          });
          pendingStartResolveRef.current = null;
          pendingStartRejectRef.current = null;
        } else if (message.type === 'paused') {
          pendingPauseAcksRef.current[0]?.();
        } else if (message.type === 'reconnecting') {
          const reasonCode =
            typeof message.reason === 'string' ? message.reason : 'upstream_closed';
          setUpstreamReconnect((current) => current ?? { startedAt: Date.now(), reasonCode });
        } else if (message.type === 'reconnected') {
          setUpstreamReconnect(null);
        } else if (message.type === 'transcribed') {
          pendingTranscriptionResolveRef.current?.();
          pendingTranscriptionResolveRef.current = null;
          pendingTranscriptionRejectRef.current = null;
        } else if (message.type === 'result') {
          setIsProcessing(false);
          const resultId = (message.data as { id?: string | number } | null)?.id;
          setIcdResultId(resultId == null ? null : String(resultId));
          pendingResolveRef.current?.(message.data);
          pendingResolveRef.current = null;
          pendingRejectRef.current = null;
          if (icdSlowHintTimeoutRef.current) {
            clearTimeout(icdSlowHintTimeoutRef.current);
          }
          if (icdGiveUpTimeoutRef.current) {
            clearTimeout(icdGiveUpTimeoutRef.current);
          }
          icdSlowHintTimeoutRef.current = setTimeout(() => {
            setIcdProcessingSlow(true);
          }, ICD_SLOW_HINT_MS);
          icdGiveUpTimeoutRef.current = setTimeout(() => {
            setIcdCodes([]);
            setIcdStatus('ready');
            disconnect();
          }, ICD_GIVE_UP_MS);
        } else if (message.type === 'icd_codes') {
          if (icdSlowHintTimeoutRef.current) {
            clearTimeout(icdSlowHintTimeoutRef.current);
            icdSlowHintTimeoutRef.current = null;
          }
          if (icdGiveUpTimeoutRef.current) {
            clearTimeout(icdGiveUpTimeoutRef.current);
            icdGiveUpTimeoutRef.current = null;
          }
          const data = Array.isArray(message.data) ? (message.data as DiagnosedIcd[]) : [];
          setIcdCodes(data.map((entry) => normalizeDiagnosedIcdEntry(entry)));
          setIcdStatus('ready');
          setIcdCodesReceivedAt(Date.now());
          disconnect();
        } else if (message.type === 'error') {
          setIsProcessing(false);
          if (icdSlowHintTimeoutRef.current) {
            clearTimeout(icdSlowHintTimeoutRef.current);
            icdSlowHintTimeoutRef.current = null;
          }
          if (icdGiveUpTimeoutRef.current) {
            clearTimeout(icdGiveUpTimeoutRef.current);
            icdGiveUpTimeoutRef.current = null;
          }
          setIcdStatus('idle');
          const code = typeof message.code === 'string' ? message.code : 'unknown';
          if (pendingStartRejectRef.current) {
            pendingStartRejectRef.current({ code, ...message });
            pendingStartResolveRef.current = null;
            pendingStartRejectRef.current = null;
          } else if (pendingTranscriptionRejectRef.current) {
            pendingTranscriptionRejectRef.current({ code, ...message });
            pendingTranscriptionResolveRef.current = null;
            pendingTranscriptionRejectRef.current = null;
          } else if (pendingRejectRef.current) {
            pendingRejectRef.current({ code, ...message });
            pendingResolveRef.current = null;
            pendingRejectRef.current = null;
          } else {
            serverErrorRef.current = {
              code,
              correlationId:
                typeof message.correlationId === 'string' ? message.correlationId : undefined,
            };
          }
        }
      };
    });
  }, [disconnect]);

  const start = useCallback((metadata: RecordingMetadata): Promise<StartResult> => {
    const webSocket = webSocketRef.current;
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('WebSocket not connected'));
    }
    const {
      interactionId,
      language,
      anamnesisId,
      visitReasonId,
      medatixxId,
      fullname,
      birthdate,
      notes,
      withoutRecording,
      sameRecorder,
    } = metadata;
    webSocket.send(
      JSON.stringify(
        interactionId
          ? { type: 'resume', interactionId, ...(sameRecorder ? { sameRecorder: true } : {}) }
          : {
              type: 'start',
              language,
              anamnesisId,
              visitReasonId,
              medatixxId,
              fullname,
              birthdate,
              ...(notes ? { notes } : {}),
              ...(withoutRecording ? { withoutRecording: true } : {}),
            }
      )
    );
    return new Promise<StartResult>((resolve, reject) => {
      pendingStartResolveRef.current = resolve;
      pendingStartRejectRef.current = reject;
    });
  }, []);

  const pause = useCallback((): Promise<void> => {
    const webSocket = webSocketRef.current;
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
      return Promise.resolve();
    }
    webSocket.send(JSON.stringify({ type: 'pause' }));
    return new Promise<void>((resolve) => {
      // A timed-out pause stays queued, so its late ack can't release the next pause.
      const timeout = setTimeout(resolve, PAUSE_ACK_TIMEOUT_MS);
      const settle = () => {
        clearTimeout(timeout);
        pendingPauseAcksRef.current = pendingPauseAcksRef.current.filter((p) => p !== settle);
        resolve();
      };
      pendingPauseAcksRef.current.push(settle);
    });
  }, []);

  const sendNotes = useCallback((notes: ManualNotes): void => {
    const webSocket = webSocketRef.current;
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
      return;
    }
    webSocket.send(JSON.stringify({ type: 'notes', notes }));
  }, []);

  const CHUNK_SIZE = 256 * 1024;
  const DRAIN_INTERVAL_MS = 25;
  const DRAIN_TIMEOUT_MS = 30_000;

  const waitForBufferedAmountBelow = async (ws: WebSocket, threshold = 0): Promise<void> => {
    const startedAt = Date.now();

    while (ws.bufferedAmount > threshold) {
      if (ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
        throw new Error('WebSocket closed while sending audio');
      }

      if (Date.now() - startedAt > DRAIN_TIMEOUT_MS) {
        throw new Error('WebSocket drain timeout');
      }

      await new Promise((resolve) => setTimeout(resolve, DRAIN_INTERVAL_MS));
    }
  };
  const sendChunk = useCallback(
    async (chunk: Blob): Promise<void> => {
      const webSocket = webSocketRef.current;
      if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket not connected');
      }

      let offset = 0;

      while (offset < chunk.size) {
        if (webSocket.readyState !== WebSocket.OPEN) {
          throw new Error('WebSocket not connected');
        }

        const slice = chunk.slice(offset, offset + CHUNK_SIZE);
        const buffer = await slice.arrayBuffer();

        webSocket.send(buffer);
        offset += CHUNK_SIZE;

        await waitForBufferedAmountBelow(webSocket, CHUNK_SIZE);
      }

      await waitForBufferedAmountBelow(webSocket, 0);
    },
    [CHUNK_SIZE]
  );

  const requestTranscription = useCallback(async (): Promise<void> => {
    const webSocket = webSocketRef.current;
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }
    webSocket.send(JSON.stringify({ type: 'transcribe' }));
    return new Promise<void>((resolve, reject) => {
      pendingTranscriptionResolveRef.current = resolve;
      pendingTranscriptionRejectRef.current = reject;
    });
  }, []);

  const end = useCallback(async (): Promise<unknown> => {
    const webSocket = webSocketRef.current;
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }
    setIsProcessing(true);
    webSocket.send(JSON.stringify({ type: 'end' }));
    return new Promise<unknown>((resolve, reject) => {
      pendingResolveRef.current = resolve;
      pendingRejectRef.current = reject;
    });
  }, []);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    start,
    pause,
    sendChunk,
    sendNotes,
    requestTranscription,
    end,
    disconnect,
    consumeServerError,
    isConnected,
    isProcessing,
    upstreamReconnect,
    icdCodes,
    icdStatus,
    icdProcessingSlow,
    icdCodesReceivedAt,
    icdResultId,
  };
}
